import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type {
  SalesContractDetail,
  SalesContractItem,
} from '@/utils/approval/salesContract';

/** 작성·조회 화면이 이 클래스로 Print·삭제 버튼을 그림 */
export const CARD_CLASS = 'sc-card';
export const PRINT_CELL_CLASS = 'sc-print';
export const ACTION_CELL_CLASS = 'sc-action';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시: 통화가 USD 면 그대로, 그 외는 number_format. 통화를 못 받으면 그대로 */
const formatUnitPrice = (v: unknown, currCd: string) => {
  const raw = text(v);
  if (!currCd || currCd === 'USD') return raw;
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : raw;
};

const InfoTable = ({ item, t }: { item: SalesContractItem; t: TFunction }) => {
  const { card } = item;
  const header = card.header ?? {};
  const contractNo = text(card.salesContrNo ?? header.salesContrNo);
  const oz = card.ozPrint ?? {};
  const brand = text(header.brand ?? card.details?.[0]?.brand);
  return (
    <table className="table table-bordered">
      <colgroup>
        <col width="9%" />
        <col width="19%" />
        {Array.from({ length: 8 }, (_, i) => (
          <col key={i} width="9%" />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th className="text-center bg-info" colSpan={6}>
            {t('({{contractNo}}, {{rev}}차)', {
              contractNo,
              rev: item.aprvRev,
            })}
          </th>
          <th
            className={`text-right bg-info ${PRINT_CELL_CLASS}`}
            colSpan={2}
            data-oz-url={text(oz.url)}
            data-oz-ozr={text(oz.gerpOzrNm)}
            data-oz-odi={text(oz.gerpOdiNm)}
            data-oz-pcount={text(oz.pcount)}
            data-oz-args1={text(oz.args1)}
          />
          <th
            className={`text-left bg-info ${ACTION_CELL_CLASS}`}
            colSpan={2}
          />
        </tr>
        <tr className="bg-title">
          <td>{t('브랜드')}</td>
          <td>{t('벤더')}</td>
          <td>{t('원산지 국가')}</td>
          <td>{t('인코텀즈 유형')}</td>
          <td>{t('화폐')}</td>
          <td>{t('계약일자')}</td>
          <td>{t('지불조건')}</td>
          <td>{t('선적항')}</td>
          <td>{t('합의납기일 최종')}</td>
          <td>{t('패킹기준')}</td>
        </tr>
        <tr className="text-center">
          <td>{brand}</td>
          <td>
            {text(header.poVdCd)}
            <br />
            <span className="vendor-name">({text(header.vdNm)})</span>
          </td>
          <td>{text(header.originCd)}</td>
          <td>{text(header.inctType)}</td>
          <td>{text(header.currCd)}</td>
          <td>{text(header.salesContrDate)}</td>
          <td>{text(header.payTerm)}</td>
          <td>{text(header.sPort)}</td>
          <td>{text(header.salesMainfinishsched)}</td>
          <td>{text(header.packStd)}</td>
        </tr>
      </tbody>
    </table>
  );
};

/** 레거시: 시즌 칸은 시즌 첫 행에만 두고 시즌 행 수만큼 합침 */
const DetailRow = ({
  row,
  currCd,
}: {
  row: SalesContractDetail;
  currCd: string;
}) => {
  const rowSpan = Number(row.seasonRowspan) || 1;
  const isFirst = Number(row.seasonNum) === 1 || !row.seasonNum;
  return (
    <tr>
      {isFirst && (
        <td className="text-center" rowSpan={rowSpan > 1 ? rowSpan : undefined}>
          {text(row.season)}
        </td>
      )}
      <td className="text-center">{text(row.styleNo)}</td>
      <td className="text-center">{text(row.stylePoNo)}</td>
      <td className="text-center">{text(row.colorCd)}</td>
      <td className="text-right">{formatUnitPrice(row.unitPrice, currCd)}</td>
    </tr>
  );
};

const DetailTable = ({
  item,
  t,
}: {
  item: SalesContractItem;
  t: TFunction;
}) => {
  const details = item.card.details ?? [];
  if (!details.length) return null;
  const currCd = text(item.card.header?.currCd);
  return (
    <table className="table table-bordered">
      <colgroup>
        <col width="29%" />
        {Array.from({ length: 4 }, (_, i) => (
          <col key={i} width="18%" />
        ))}
      </colgroup>
      <tbody>
        <tr className="bg-title">
          <th>{t('시즌')}</th>
          <th>{t('스타일')}</th>
          <th>{t('발주번호')}</th>
          <th>{t('컬러코드')}</th>
          <th>{t('가공임')}</th>
        </tr>
        {details.map((row, i) => (
          <DetailRow key={i} row={row} currCd={currCd} />
        ))}
      </tbody>
    </table>
  );
};

/** 레거시 appr_sub_view_forms_sales_contract_mstr — 계약마다 카드 1개 */
const Card = ({ item, t }: { item: SalesContractItem; t: TFunction }) => (
  <div
    className={CARD_CLASS}
    data-item-id={item.aprvId}
    data-label={item.label}
    data-aprv-rev={item.aprvRev}
  >
    <InfoTable item={item} t={t} />
    <DetailTable item={item} t={t} />
  </div>
);

/** 저장 본문 — 고른 순서대로 카드 */
export const buildSalesContractHtml = (
  items: SalesContractItem[],
  t: TFunction,
): string =>
  items.length
    ? renderToStaticMarkup(
        <>
          {items.map((item) => (
            <Card key={item.aprvId} item={item} t={t} />
          ))}
        </>,
      )
    : '';
